import "./index.css";
import { clsx } from "clsx";
import { InboxIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NotificationsRepository } from "@/api/notifications/repository";
import { NotificationsService } from "@/api/notifications/service";
import { HeaderMenu } from "@/components/Notifications/components/HeaderMenu";
import { PAGINATION_SIZE } from "@/constants/index";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { useNotificationsStore } from "@/store/notifications/slice";
import type { INotificationsPagination } from "@/types/notifications";
import { EmptyState, Text } from "@/ui-kit";
import { useFetchPaginatedNotifications } from "../../hooks/useFetchPaginatedNotifications";
import { LoadingState } from "../LoadingState";
import { Notification } from "../Notification";

export const Notifications = () => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement | null>(null);
  const notifications = useNotificationsStore((s) => s.notifications);
  const notificationsLoaded = useNotificationsStore(
    (s) => s.notificationsLoaded,
  );
  const { isMobile } = useBreakpoints();

  const repository = useMemo(() => {
    return new NotificationsRepository(new NotificationsService(), {
      abort: true,
    });
  }, []);

  const pagination = useRef<INotificationsPagination>({
    page: 1,
    size: PAGINATION_SIZE,
  });

  const { fetchNotifications } = useFetchPaginatedNotifications();

  useEffect(() => {
    repository.getUnreadCount().then((response) => {
      useNotificationsStore.getState().setCount(response);
    });

    fetchNotifications(pagination.current).then(() => {
      pagination.current.page += 1;
    });
  }, [fetchNotifications, pagination, repository]);

  useEffect(() => {
    const OFFSET = 60;

    const handleScroll = () => {
      const scrollContainer = listRef.current;
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - OFFSET;

      if (isNearBottom) {
        fetchNotifications(pagination.current).then(() => {
          pagination.current.page += 1;
        });
      }
    };

    const scrollContainer = listRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [fetchNotifications, pagination]);

  useEffect(() => {
    return () => {
      useNotificationsStore.getState().setNotifications([]);
      useNotificationsStore.getState().setNotificationsLoaded(false);
    };
  }, []);

  const notificationsJSX = notifications.map((item, i: number) => {
    return <Notification key={`${item.id}-${i}`} item={item} />;
  });

  return (
    <div className={clsx("notifications", isMobile && "notifications--mobile")}>
      {!isMobile && (
        <header className="notifications-header">
          <Text weight="bold" as="p" className="notifications-header__title">
            {t("label.notifications")}
          </Text>
          {notificationsLoaded && <HeaderMenu />}
        </header>
      )}
      <div className="notifications-list" ref={listRef}>
        {notificationsLoaded ? (
          notifications.length === 0 ? (
            <EmptyState
              className="notifications-list__empty-state"
              icon={<InboxIcon size="2.25rem" />}
              text={t("banner.emptyNotifications.noItems.title")}
            />
          ) : (
            notificationsJSX
          )
        ) : (
          <>
            <LoadingState />
            <LoadingState />
          </>
        )}
      </div>
    </div>
  );
};
